import React from "react";

import { ContactInfo } from "~/components";
import { AppointmentDetails } from "~/stores";
import { icons } from "~/ui";
import { tw } from "~/utils";

const CARD_SIZES = {
  default: {
    card: "p-5",
    row: "items-start",
    avatar: "size-33.5",
    placeholderIcon: "size-16",
    details: "gap-5 py-3",
    name: "text-base",
    line: "text-base",
  },
  compact: {
    card: "p-4",
    row: "items-center",
    avatar: "size-25",
    placeholderIcon: "size-12",
    details: "gap-2",
    name: "text-base",
    line: "text-base",
  },
} as const;

interface AppointmentDetailsCardProps {
  appointmentDetails: AppointmentDetails;
  hideContactInfo?: boolean;
  size?: keyof typeof CARD_SIZES;
}

export const AppointmentDetailsCard = ({
  appointmentDetails,
  hideContactInfo = false,
  size = "default",
}: AppointmentDetailsCardProps) => {
  const sizeClasses = CARD_SIZES[size];

  if (!appointmentDetails?.provider || !appointmentDetails?.timeSlot) {
    return (
      <section>
        <div className="bg-lightLavender/50 rounded-lg p-2 md:space-y-2">
          <h2 className="font-medium text-gray-800">
            No appointment details available
          </h2>
        </div>
        {!hideContactInfo && <ContactInfo className="hidden md:block" />}
      </section>
    );
  }

  return (
    <section>
      <div
        className={tw("bg-lightLavender/30 rounded-[6px]", sizeClasses.card)}
      >
        <div className={tw("flex gap-3", sizeClasses.row)}>
          <div
            className={tw(
              "shrink-0 overflow-hidden rounded-full",
              sizeClasses.avatar,
            )}
          >
            {appointmentDetails.provider.image ? (
              <img
                src={appointmentDetails.provider.image}
                alt={appointmentDetails.provider.name}
                className="size-full object-cover"
              />
            ) : (
              <div className="bg-lightLavender flex size-full items-center justify-center">
                <icons.outline.UserIcon
                  className={tw("text-indigo", sizeClasses.placeholderIcon)}
                />
              </div>
            )}
          </div>

          <div
            className={tw("flex flex-col justify-center", sizeClasses.details)}
          >
            <h2 className={tw("font-medium text-gray-800", sizeClasses.name)}>
              {appointmentDetails.provider.name},{" "}
              {appointmentDetails.provider.title}
            </h2>
            <div className="flex flex-col gap-1">
              <div
                className={tw(
                  "flex items-center gap-1 text-gray-800",
                  sizeClasses.line,
                )}
              >
                <icons.outline.CalendarIcon className="size-4.5 shrink-0" />
                <span>{appointmentDetails.date}</span>
              </div>
              <div
                className={tw(
                  "flex items-center gap-1 text-gray-800",
                  sizeClasses.line,
                )}
              >
                <icons.outline.ClockIcon className="size-4.5 shrink-0" />
                <span>{appointmentDetails.formattedTime}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!hideContactInfo && <ContactInfo className="hidden md:block" />}
    </section>
  );
};
